/**
 * Task 36: Add Follow-Up Modal
 * Lightweight modal for creating candidate follow-ups
 */

import React, { useState } from 'react';
import { Calendar, X, CheckCircle, AlertCircle } from 'lucide-react';
import axiosInstance from '../../config/axiosConfig';
import toast from '../../utils/toast';
import eventBus from '../../utils/eventBus';

const FOLLOWUP_TYPES = [
  { value: 'call', label: 'Call' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
  { value: 'document', label: 'Document' },
  { value: 'interview', label: 'Interview' },
  { value: 'other', label: 'Other' }
];

const AddFollowUpModal = ({ isOpen, onClose, candidateId, candidateName, onSuccess }) => {
  const [formData, setFormData] = useState({
    followup_type: 'call',
    notes: '',
    target_date: '',
    reminder: false
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  if (!isOpen) return null;

  const validate = () => {
    const newErrors = {};
    
    if (!formData.target_date) {
      newErrors.target_date = 'Target date is required';
    } else {
      const targetDate = new Date(formData.target_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (targetDate < today) {
        newErrors.target_date = 'Target date cannot be in the past';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axiosInstance.post('/followups/', {
        candidate_id: candidateId,
        followup_type: formData.followup_type,
        notes: formData.notes,
        target_date: formData.target_date,
        reminder: formData.reminder
      });
      
      // Emit analytics event
      try {
        eventBus.emit('followup_added', {
          recruiter_id: null, // Backend auto-assigns
          candidate_id: candidateId,
          followup_type: formData.followup_type,
          target_date: formData.target_date,
          channel: 'desktop',
          source: 'AddFollowUpModal',
          timestamp: new Date().toISOString(),
          metadata: {
            candidate_name: candidateName,
            has_notes: !!formData.notes,
            reminder_enabled: formData.reminder
          }
        });
      } catch (eventBusError) {
        console.warn('EventBus failed to emit followup_added event:', eventBusError);
      }
      
      toast.success('Follow-up added successfully! 📅');
      
      if (onSuccess) {
        onSuccess(response.data);
      }
      
      // Reset form
      setFormData({
        followup_type: 'call',
        notes: '',
        target_date: '',
        reminder: false
      });
      
      onClose();
    } catch (error) {
      console.error('Failed to create follow-up:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to add follow-up';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white dark:bg-navy-dark rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-heading font-bold text-navy-900 dark:text-white">
              Add Follow-Up
            </h2>
            {candidateName && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                For: {candidateName}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Follow-up Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Follow-up Type *
            </label>
            <select
              value={formData.followup_type}
              onChange={(e) => handleChange('followup_type', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              {FOLLOWUP_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Target Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Target Date *
            </label>
            <div className="relative">
              <input
                type="date"
                value={formData.target_date}
                onChange={(e) => handleChange('target_date', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white ${
                  errors.target_date ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              <Calendar className="absolute right-3 top-2.5 w-5 h-5 text-gray-400 pointer-events-none" />
            </div>
            {errors.target_date && (
              <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.target_date}
              </p>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              rows={3}
              placeholder="Add any notes about this follow-up..."
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 resize-none"
            />
          </div>

          {/* Reminder Toggle */}
          <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <input
              type="checkbox"
              id="reminder"
              checked={formData.reminder}
              onChange={(e) => handleChange('reminder', e.target.checked)}
              className="w-4 h-4 text-teal border-gray-300 rounded focus:ring-teal"
            />
            <label htmlFor="reminder" className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              Enable reminder (passive)
            </label>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-teal hover:bg-teal-dark text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Save Follow-Up
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddFollowUpModal;
